import { type DefaultError, useMutation, useQueryClient } from '@tanstack/react-query';
import { Undo2 } from 'lucide-react';
import { invalidateAssetDataForNodeChange } from '@/hooks/websocket/mergeAssetPayload';
import { getAllDescendantIds, useTreeStore } from '@/hooks/data/tree/useTreeStore';
import { useAuthenticatedQueryFn } from '@/hooks/useAuthenticatedQuery';
import { useUploadStore } from '@/hooks/data/inspector/useUploadStore';
import { useCommandContext } from '@/context/CommandContext';
import { trashNodesQuery } from '@/services/trash.service';
import { type TrashNodesResponse } from '@/types/trash';
import { useTreeContext } from '@/context/TreeContext';
import { useToast } from '@/components/ui/use-toast';
import { DAMNodeType, NodeType } from '@/types/tree';
import { ToastAction } from '@/components/ui/toast';
import { QueryKey } from '@/lib/queryKeys';
import { useTranslations } from 'next-intl';

export const useTrashNodesMutation = () => {
  const queryClient = useQueryClient();
  const trashNodesQueryFn = useAuthenticatedQueryFn(trashNodesQuery);
  const rootFolders = useTreeStore((state) => state.rootFolders);
  const context = useTreeContext();
  const tToasts = useTranslations('toasts');
  const tTree = useTranslations('tree');
  const tCommon = useTranslations('common');
  const { toast } = useToast();
  const { undo } = useCommandContext();

  const getItemLabel = (type: string | undefined, count: number) => {
    switch (type) {
      case DAMNodeType.Asset:
      case NodeType.Assets:
        return tTree('asset', { count });
      case DAMNodeType.Album:
      case NodeType.Albums:
        return tTree('album', { count });
      case DAMNodeType.Folder:
      case NodeType.Folders:
        return tTree('folder', { count });
      default:
        return tTree('item', { count });
    }
  };

  const getTitleItemLabel = (label: string) => {
    if (!label) {
      return label;
    }

    return label.charAt(0).toUpperCase() + label.slice(1);
  };

  // `params.nodes` addresses the shared node-tree table (folders/albums/assets alike) — what
  // the backend endpoint needs. For a multi-selection of assets those are node ids, a
  // different id space from the asset ids the upload store keys its files by (types/asset.ts:
  // `id` vs `nodeId`), so the caller passes the real asset ids separately when it has them
  // (delete-node-alert-component.tsx). Fall back to `nodes` for everything else — a single
  // selection already sends the asset's own id there, and folders/albums have no upload-list
  // presence to match anyway.
  const getUploadStoreIds = (params: { nodes?: Array<string>; assetIds?: Array<string> }): Array<string> =>
    params.assetIds && params.assetIds.length > 0 ? params.assetIds : (params.nodes ?? []);

  const { mutateAsync: trashNodesMutation } = useMutation<
    TrashNodesResponse,
    DefaultError,
    Parameters<typeof trashNodesQuery>['0'] & { assetIds?: Array<string> },
    { prevTreeSearch?: Array<[unknown, any]>; alreadyTrashedIds?: Array<string> }
  >({
    onMutate: async (params) => {
      await queryClient.cancelQueries({ queryKey: [QueryKey.TreeSearch] });
      const prevTreeSearch = queryClient.getQueriesData<any>({ queryKey: [QueryKey.TreeSearch] });
      let alreadyTrashedIds: Array<string> = [];
      const uploadStoreIds = getUploadStoreIds(params ?? {});

      if (uploadStoreIds.length > 0) {
        // Some of these ids may already be hidden by an unrelated trash action (or another
        // node in the same batch overlapping one already trashed) — remember which, so a
        // failed request only un-hides the ones this call itself hid.
        alreadyTrashedIds = useUploadStore.getState().getTrashedFileIds(uploadStoreIds);
        // Optimistic: hides the upload-list row immediately, before the request resolves.
        useUploadStore.getState().setFileTrashed(uploadStoreIds, true);
      }

      if (Array.isArray(params?.nodes) && params.nodes.length > 0) {
        prevTreeSearch.forEach(([key, data]) => {
          if (!Array.isArray(data)) {
            return;
          }

          const filtered = data.filter((item: any) => !params.nodes?.includes(item?.id));
          if (filtered.length !== data.length) {
            queryClient.setQueryData(key, filtered);
          }
        });
      }

      return { prevTreeSearch, alreadyTrashedIds };
    },
    mutationFn: (params) => {
      // Clean up descendant node IDs from the open IDs arrays BEFORE the mutation
      if (params?.nodes && rootFolders.length > 0) {
        const allIdsToRemove: Array<string> = [];

        // For each node being deleted, collect it and all its descendants
        params.nodes.forEach((nodeId) => {
          allIdsToRemove.push(nodeId);
          const descendantIds = getAllDescendantIds(nodeId, rootFolders);
          allIdsToRemove.push(...descendantIds);
        });

        // Remove all these IDs from the tracking arrays immediately
        if (allIdsToRemove.length > 0) {
          context.removeNodeIds(allIdsToRemove);
        }
      }

      // assetIds is client-side only (upload-list matching) — the backend only expects
      // nodes/type/undoable, so it's dropped here rather than riding along on the wire.
      return trashNodesQueryFn({ nodes: params.nodes, type: params.type, undoable: params.undoable });
    },
    onError: (error, params, ctx) => {
      ctx?.prevTreeSearch?.forEach(([key, data]) => {
        queryClient.setQueryData(key as any, data);
      });

      const uploadStoreIds = getUploadStoreIds(params ?? {});
      if (uploadStoreIds.length > 0) {
        // Only un-hide ids this call actually hid — one already trashed before this
        // mutation ran must stay trashed regardless of how this request turns out.
        const idsToUnhide = uploadStoreIds.filter((id) => !ctx?.alreadyTrashedIds?.includes(id));
        if (idsToUnhide.length > 0) {
          useUploadStore.getState().setFileTrashed(idsToUnhide, false);
        }
      }
    },
    onSettled: (response, error, params) => {
      const count = params?.nodes?.length ?? 0;
      const itemLabel = getItemLabel(params?.type, count);
      const titleItemLabel = getTitleItemLabel(itemLabel);
      toast({
        title: tToasts('trashNodesMutation.title', { itemLabel: titleItemLabel }),
        description: tToasts('trashNodesMutation.description', { itemLabel, count }),
        ...(params.undoable && {
          action: (
            <ToastAction onClick={undo} altText={tToasts('undoNodeDelete')} icon={Undo2}>
              {tCommon('undo')}
            </ToastAction>
          ),
        }),
      });

      void queryClient.invalidateQueries({ queryKey: [QueryKey.Trash], refetchType: 'all' });
      void invalidateAssetDataForNodeChange(queryClient);
      void queryClient.invalidateQueries({ queryKey: [QueryKey.Duplicates] });
      void queryClient.invalidateQueries({ queryKey: [QueryKey.Tree] });
      void queryClient.invalidateQueries({ queryKey: [QueryKey.TreeSearch] });
      void queryClient.invalidateQueries({ queryKey: [QueryKey.ShareList] });
      // Invalidate home only if a folder or an album is deleted
      // because assets are not shown under favorites anyway
      if (params.type === DAMNodeType.Folder || params.type === DAMNodeType.Album) {
        void queryClient.invalidateQueries({ queryKey: [QueryKey.Home] });
      }
    },
  });

  return {
    trashNodesMutation,
  };
};
